import React, { useEffect, useMemo, useRef, useState } from 'react'
import i18n from 'simple-react-i18n'
import { isNil } from 'lodash'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Button, Dialog, DialogActions, DialogContent, Grid, Stack } from '@mui/material'
import { CardTable, NewTable, WrapperBox } from 'components/datatable/NewTable'
import MessageCard from 'components/card/MessageCard'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import Input from 'components/forms/Input'
import Select from 'components/forms/Select'
import useSandreList from 'utils/customHook/useSandreList'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'
import { PATH_REFERENCIAL } from 'home/constants/RouteConstants'
import { darkBlue, selectedColor } from 'utils/constants/ColorTheme'
import TaxonAction from 'referencial/components/taxon/actions/TaxonAction'
import ReferencialAction from '../../action/ReferencialAction'

const itemId = item => item.code ?? item.tempId

const EcoFriendlyFeaturesApp = () => {
    const dispatch = useDispatch()

    const {
        accountUser,
        sandreCodes,
        ecobioTraits,
        ecobioModalities,
        allEcobioTaxonTraits,
        taxons,
    } = useSelector(store => ({
        accountUser: store.AccountReducer.accountUser,
        ecobioTraits: store.TaxonReducer.ecobioTraits,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        ecobioModalities: store.TaxonReducer.ecobioModalities,
        allEcobioTaxonTraits: store.TaxonReducer.allEcobioTaxonTraits,
        taxons: store.TaxonReducer.taxons,
    }), shallowEqual)

    const taxonsGroupe = useSandreList('TAXONS.GROUPE')

    const [traits, setTraits] = useState([])
    const [modalities, setModalities] = useState([])
    const [selectedTraitId, setSelectedTraitId] = useState(undefined)
    const [editedTrait, setEditedTrait] = useState(undefined)
    const [editedModality, setEditedModality] = useState(undefined)
    const [selectedModality, setSelectedModality] = useState(undefined)
    const [isEditMode, setIsEditMode] = useState(false)

    const tempIdRef = useRef(0)
    const nextTempId = () => {
        tempIdRef.current -= 1
        return tempIdRef.current
    }

    const isConsultant = useMemo(() => accountUser.consultant === '1', [accountUser.consultant])

    useEffect(() => {
        dispatch(TaxonAction.fetchEcobioTraits())
        dispatch(TaxonAction.fetchEcobioModalities())
        dispatch(TaxonAction.fetchAllEcobioTaxonTraits())
        if (!sandreCodes.length) {
            dispatch(ReferencialAction.fetchSandreCodes())
        }
    }, [dispatch, sandreCodes.length])

    useEffect(() => {
        if (!taxons.length) {
            dispatch(TaxonAction.fetchTaxons())
        }
    }, [dispatch, taxons.length])

    useEffect(() => {
        setTraits(ecobioTraits)
    }, [ecobioTraits])

    useEffect(() => {
        setModalities(ecobioModalities)
    }, [ecobioModalities])

    useTitle(() => [{
        title: i18n.referencials,
        href: PATH_REFERENCIAL,
    }, {
        title: i18n.ecoFriendlyFeatures,
        href: '/referencial/ecoFriendlyFeatures',
    }], [])

    const cancelEdit = () => {
        setTraits(ecobioTraits)
        setModalities(ecobioModalities)
        setEditedTrait(undefined)
        setEditedModality(undefined)
        setIsEditMode(false)
    }

    useActions(() => {
        if (isConsultant) {
            return {}
        }
        if (isEditMode) {
            return {
                save: () => {
                    dispatch(TaxonAction.saveEcobioReferential({
                        traits,
                        modalities,
                        originalTraits: ecobioTraits,
                        originalModalities: ecobioModalities,
                    }))
                    setIsEditMode(false)
                },
                cancel: cancelEdit,
            }
        }
        return { edit: () => setIsEditMode(true) }
    }, [isConsultant, isEditMode, traits, modalities, ecobioTraits, ecobioModalities])

    const selectedTrait = useMemo(
        () => traits.find(t => Number(itemId(t)) === Number(selectedTraitId)),
        [traits, selectedTraitId],
    )

    const traitRows = useMemo(() => traits.map(trait => ({
        id: itemId(trait),
        code: trait.code,
        name: trait.name,
        group: taxonsGroupe.find(g => Number(g.code) === Number(trait.groupCode))?.name,
        colorLine: Number(itemId(trait)) === Number(selectedTraitId) ? selectedColor : undefined,
        trait,
    })), [traits, taxonsGroupe, selectedTraitId])

    const assignedTaxonCodes = useMemo(() => allEcobioTaxonTraits.reduce((acc, { traitCode, modalityCode, taxonCode }) => {
        const key = `${traitCode}#${modalityCode}`
        acc[key] = [...(acc[key] ?? []), taxonCode]
        return acc
    }, {}), [allEcobioTaxonTraits])

    const modalityRows = useMemo(() => (isNil(selectedTraitId) ? [] : modalities
        .filter(m => Number(m.traitCode) === Number(selectedTraitId))
        .map(modality => {
            const taxonCodes = assignedTaxonCodes[`${modality.traitCode}#${modality.code}`] ?? []
            return {
                code: modality.code,
                name: modality.name,
                abbreviation: modality.abbreviation,
                nbAssignments: taxonCodes.length,
                taxonCodes,
                modality,
            }
        })), [modalities, selectedTraitId, assignedTaxonCodes])

    const assignedTaxonRows = useMemo(() => (selectedModality?.taxonCodes ?? []).map(taxonCode => {
        const taxon = taxons.find(t => String(t.code) === String(taxonCode))
        return {
            code: taxonCode,
            latinName: taxon?.latinName,
        }
    }), [selectedModality, taxons])

    const onValidateTrait = () => {
        const id = itemId(editedTrait)
        if (isNil(id)) {
            setTraits(prev => [...prev, { ...editedTrait, tempId: nextTempId() }])
        } else {
            setTraits(prev => prev.map(t => (Number(itemId(t)) === Number(id) ? { ...editedTrait } : t)))
        }
        setEditedTrait(undefined)
    }

    const onDeleteTrait = trait => {
        const id = itemId(trait)
        setTraits(prev => prev.filter(t => Number(itemId(t)) !== Number(id)))
        setModalities(prev => prev.filter(m => Number(m.traitCode) !== Number(id)))
        if (Number(id) === Number(selectedTraitId)) {
            setSelectedTraitId(undefined)
        }
    }

    const onValidateModality = () => {
        const id = itemId(editedModality)
        if (isNil(id)) {
            setModalities(prev => [...prev, { ...editedModality, tempId: nextTempId() }])
        } else {
            setModalities(prev => prev.map(m => (Number(itemId(m)) === Number(id) ? { ...editedModality } : m)))
        }
        setEditedModality(undefined)
    }

    const onDeleteModality = modality => {
        const id = itemId(modality)
        setModalities(prev => prev.filter(m => Number(itemId(m)) !== Number(id)))
    }

    return (
        <Stack margin={1} spacing={1}>
            <CardTable
                title={i18n.ecoFriendlyFeatures}
                color='bannerColor'
                rows={traitRows}
                headers={[
                    { key: 'name', value: i18n.name },
                    { key: 'group', value: i18n.group },
                ]}
                onClickRow={row => setSelectedTraitId(row.id)}
                actions={isEditMode ? [{
                    icon: 'note_add',
                    tooltip: i18n.add,
                    color: darkBlue,
                    onClick: () => setEditedTrait({}),
                }] : []}
                lineActions={isEditMode ? [
                    { icon: 'edit', tooltip: i18n.edit, onClick: row => setEditedTrait({ ...row.trait }) },
                    { icon: 'delete', tooltip: i18n.delete, onClick: row => onDeleteTrait(row.trait) },
                ] : []}
            />

            {!isNil(selectedTrait) && (
                <CardTable
                    title={`${i18n.modality} - ${selectedTrait.name ?? selectedTrait.code ?? ''}`}
                    rows={modalityRows}
                    headers={[
                        { key: 'name', value: i18n.name },
                        { key: 'abbreviation', value: i18n.abbreviation },
                        { key: 'nbAssignments', value: i18n.nbAssignments },
                    ]}
                    onClickRow={row => setSelectedModality(row)}
                    actions={isEditMode ? [{
                        icon: 'note_add',
                        tooltip: i18n.add,
                        color: darkBlue,
                        onClick: () => setEditedModality({ traitCode: selectedTraitId }),
                    }] : []}
                    lineActions={isEditMode ? [
                        { icon: 'edit', tooltip: i18n.edit, onClick: row => setEditedModality({ ...row.modality }) },
                        { icon: 'delete', tooltip: i18n.delete, onClick: row => onDeleteModality(row.modality) },
                    ] : []}
                />
            )}

            {/* Popin taxons affectés à une modalité */}
            <Dialog open={!isNil(selectedModality)} onClose={() => setSelectedModality(undefined)} maxWidth='sm' fullWidth>
                <DefaultDialogTitle
                    title={`${i18n.taxons} - ${selectedModality?.name ?? ''}`}
                    onClose={() => setSelectedModality(undefined)}
                />
                <DialogContent>
                    <NewTable
                        rows={assignedTaxonRows}
                        headers={[
                            { key: 'code', value: i18n.code },
                            { key: 'latinName', value: i18n.latinName },
                        ]}
                        noDataDisplay={<MessageCard>{i18n.noDataToDisplay}</MessageCard>}
                        WrapperComponent={WrapperBox}
                        disableLocalStorage
                    />
                </DialogContent>
            </Dialog>

            {/* Popin trait */}
            <Dialog open={!isNil(editedTrait)} onClose={() => setEditedTrait(undefined)} maxWidth='sm' fullWidth>
                <DefaultDialogTitle title={i18n.trait} onClose={() => setEditedTrait(undefined)} />
                <DialogContent>
                    <Grid container spacing={1}>
                        <Grid size={6}>
                            <Input
                                title={i18n.name}
                                value={editedTrait?.name}
                                onChange={v => setEditedTrait({ ...editedTrait, name: v })}
                                obligatory
                            />
                        </Grid>
                        <Grid size={6}>
                            <Select
                                label={i18n.group}
                                value={editedTrait?.groupCode}
                                options={taxonsGroupe}
                                onChange={v => setEditedTrait({ ...editedTrait, groupCode: v })}
                            />
                        </Grid>
                        <Grid size={12}>
                            <Input
                                title={i18n.comments}
                                value={editedTrait?.comments}
                                onChange={v => setEditedTrait({ ...editedTrait, comments: v })}
                            />
                        </Grid>
                        <Grid size={12}>
                            <Input
                                title={i18n.bibliographicReference}
                                value={editedTrait?.bibliographicReferences}
                                onChange={v => setEditedTrait({ ...editedTrait, bibliographicReferences: v })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant='contained'
                        color='primary'
                        onClick={onValidateTrait}
                        disabled={isNil(editedTrait?.name) || isNil(editedTrait?.groupCode)}
                    >
                        {i18n.validate}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Popin modalité */}
            <Dialog open={!isNil(editedModality)} onClose={() => setEditedModality(undefined)} maxWidth='sm' fullWidth>
                <DefaultDialogTitle title={i18n.modality} onClose={() => setEditedModality(undefined)} />
                <DialogContent>
                    <Grid container spacing={1}>
                        <Grid size={8}>
                            <Input
                                title={i18n.name}
                                value={editedModality?.name}
                                onChange={v => setEditedModality({ ...editedModality, name: v })}
                                obligatory
                            />
                        </Grid>
                        <Grid size={4}>
                            <Input
                                title={i18n.abbreviation}
                                value={editedModality?.abbreviation}
                                onChange={v => setEditedModality({ ...editedModality, abbreviation: v })}
                            />
                        </Grid>
                        <Grid size={12}>
                            <Input
                                title={i18n.comments}
                                value={editedModality?.comments}
                                onChange={v => setEditedModality({ ...editedModality, comments: v })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant='contained'
                        color='primary'
                        onClick={onValidateModality}
                        disabled={isNil(editedModality?.name)}
                    >
                        {i18n.validate}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    )
}

export default EcoFriendlyFeaturesApp